import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { usePractice } from '../hooks/usePractice'
import { createCheckoutSession, switchPlan, getSubscription } from '../lib/supabase'
import { useToast } from '../components/Toast'
import usePageTitle from '../hooks/usePageTitle'
import Navbar from '../components/Navbar'

const FEATURES_PER_CLIENT = [
  'Up to any number of clients',
  'Unlimited practice users',
  'Unlimited receipt requests per client',
  'Automatic email sending',
  'Client portal & file uploads',
  'Lost receipt declarations',
  '7-year file retention',
]

const FEATURES_PRO = [
  'Everything in per-client plan',
  'Unlimited clients — one flat price',
  'Unlimited practice users',
  'Priority support',
  'First access to new features',
]

function PlanCard({ title, price, subtitle, features, tier, recommended, onSelect, loading, isExisting, isCurrent }) {
  return (
    <div style={{
      flex: 1, minWidth: 280, maxWidth: 380,
      border: `2px solid ${recommended ? 'var(--accent)' : 'var(--surface-3)'}`,
      borderRadius: 'var(--radius-lg)',
      background: recommended ? 'var(--white)' : 'var(--white)',
      padding: 32, display: 'flex', flexDirection: 'column',
      position: 'relative', boxShadow: recommended ? 'var(--shadow-lg)' : 'var(--shadow-sm)',
    }}>
      {recommended && (
        <div style={{
          position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)',
          background: 'var(--accent)', color: 'white',
          padding: '3px 16px', borderRadius: 100,
          fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.05em',
          textTransform: 'uppercase', whiteSpace: 'nowrap',
        }}>
          Most popular
        </div>
      )}

      <div style={{ marginBottom: 8, fontSize: '0.82rem', fontWeight: 700, color: 'var(--ink-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {title}
      </div>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 4 }}>
        <span style={{ fontFamily: 'var(--font-display)', fontSize: '2.4rem', lineHeight: 1 }}>{price}</span>
        <span style={{ color: 'var(--ink-muted)', fontSize: '0.85rem' }}>/month</span>
      </div>

      <div style={{ fontSize: '0.82rem', color: 'var(--ink-muted)', marginBottom: 28, lineHeight: 1.5 }}>
        {subtitle}
      </div>

      <div style={{ flex: 1, display: 'grid', gap: 10, marginBottom: 28 }}>
        {features.map(f => (
          <div key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: '0.875rem' }}>
            <span style={{ color: 'var(--green)', fontWeight: 700, flexShrink: 0, marginTop: 1 }}>✓</span>
            <span>{f}</span>
          </div>
        ))}
      </div>

      <button
        className={`btn btn-lg ${recommended ? 'btn-primary' : 'btn-secondary'}`}
        style={{ width: '100%', justifyContent: 'center' }}
        onClick={() => onSelect(tier)}
        disabled={loading || isCurrent}
      >
        {loading ? 'Updating…' : isCurrent ? '✓ Current plan' : isExisting ? 'Switch to this plan →' : 'Get started →'}
      </button>
    </div>
  )
}

export default function Pricing() {
  const { user, loading: authLoading } = useAuth()
  const { practiceId, role } = usePractice()
  const toast      = useToast()
  const navigate   = useNavigate()
  usePageTitle('Billing')
  const [params]   = useSearchParams()
  const isNew      = params.get('new') === '1'
  const [loadingTier, setLoadingTier]   = useState(null)
  const [existingSub, setExistingSub]   = useState(null)
  const [confirmTier, setConfirmTier]   = useState(null)

  useEffect(() => {
    if (user && practiceId) {
      // Always fetch subscription from the practice owner's row
      getSubscription(practiceId).then(({ data }) => setExistingSub(data))
    }
  }, [user, practiceId])

  // Non-owners shouldn't be on the pricing page — redirect to dashboard
  useEffect(() => {
    if (!authLoading && user && role && role !== 'owner') {
      navigate('/dashboard')
    }
  }, [user, role, authLoading])

  if (authLoading) return (
    <div className="loading-screen"><div className="spinner" /></div>
  )

  // Called when bookkeeper clicks a plan button
  const handleSelect = (tier) => {
    if (!user) { navigate('/signup'); return }

    if (existingSub?.subscription_status === 'active') {
      if (existingSub.subscription_tier === tier) {
        toast('You\'re already on this plan', 'default')
        return
      }
      // Show confirmation modal before making any charge
      setConfirmTier(tier)
      return
    }

    // New subscriber or trialing — go straight to Stripe checkout
    doCheckout(tier)
  }

  const doCheckout = async (tier) => {
    setLoadingTier(tier)
    const { error } = await createCheckoutSession(tier)
    setLoadingTier(null)
    if (error) toast(error.message || 'Could not start checkout — please try again', 'error')
  }

  // Called after confirmation modal is accepted
  const doSwitch = async () => {
    const tier = confirmTier
    setConfirmTier(null)
    setLoadingTier(tier)
    const { error } = await switchPlan(tier)
    setLoadingTier(null)
    if (error) {
      toast('Could not switch plan: ' + error.message, 'error')
    } else {
      toast(`Switched to ${tier === 'pro' ? 'Pro' : 'Per client'} plan ✓`, 'success')
      navigate('/settings?tab=billing')
    }
  }

  // Build confirmation message based on the switch direction
  const buildConfirmDetails = (tier) => {
    if (!existingSub) return null
    const clientCount = existingSub.client_count ?? 0
    const billingDate = existingSub.current_period_ends_at
      ? new Date(existingSub.current_period_ends_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })
      : 'your next billing date'

    if (tier === 'pro') {
      const currentCost = `£${Math.max(1, clientCount) * 3}/month`
      return {
        title: 'Switch to Pro',
        from: `Per client (${currentCost})`,
        to: '£25/month unlimited',
        charge: '£25.00',
        when: billingDate,
        note: `Nothing changes today. You stay on your current plan and can continue adding clients as normal. On ${billingDate}, your plan switches to Pro and you'll be charged £25. From that point, all clients are included at no extra cost.`,
      }
    } else {
      const newCost = `£${Math.max(1, clientCount) * 3}/month`
      return {
        title: 'Switch to Per client',
        from: '£25/month unlimited',
        to: `${newCost} (${clientCount} active client${clientCount !== 1 ? 's' : ''} × £3)`,
        charge: `£${Math.max(1, clientCount) * 3}.00`,
        when: billingDate,
        note: `Nothing changes today. You stay on your current plan and can continue adding clients as normal. On ${billingDate}, your plan switches to Per client. Your bill will be based on your active client count at that point — currently ${clientCount} client${clientCount !== 1 ? 's' : ''} at £3 each.`,
      }
    }
  }

  const confirmDetails = confirmTier ? buildConfirmDetails(confirmTier) : null

  return (
    <div style={{ minHeight: '100vh', background: 'var(--surface)' }}>
      {user && <Navbar />}

      <main style={{ padding: user ? '48px 24px' : '64px 24px', maxWidth: 900, margin: '0 auto' }}>

        {/* Header */}
        {!user && (
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <Link to="/login" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, textDecoration: 'none', marginBottom: 32 }}>
              <div style={{ width: 40, height: 40, background: 'var(--accent)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem' }}>📋</div>
              <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', color: 'var(--ink)' }}>Receipt Chaser</span>
            </Link>
          </div>
        )}

        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          {isNew && (
            <div style={{
              display: 'inline-block',
              background: 'var(--green-light)', color: 'var(--green)',
              border: '1.5px solid var(--green)',
              borderRadius: 100, padding: '4px 16px',
              fontSize: '0.82rem', fontWeight: 700, marginBottom: 20,
            }}>
              🎉 Your 30-day free trial has started
            </div>
          )}
          <h1 style={{ marginBottom: 12 }}>Simple, transparent pricing</h1>
          <p style={{ color: 'var(--ink-muted)', fontSize: '1rem', maxWidth: 520, margin: '0 auto' }}>
            Choose the plan that suits your practice. All plans include the full feature set — no hidden limits.
          </p>
        </div>

        {/* Plans */}
        <div style={{ display: 'flex', gap: 24, justifyContent: 'center', flexWrap: 'wrap', alignItems: 'stretch' }}>
          <PlanCard
            title="Per client"
            price="£3"
            subtitle="per client, per month — pay only for what you use"
            features={FEATURES_PER_CLIENT}
            tier="per_client"
            recommended={false}
            onSelect={handleSelect}
            loading={loadingTier === 'per_client'}
            isExisting={existingSub?.subscription_status === 'active'}
            isCurrent={existingSub?.subscription_tier === 'per_client' && existingSub?.subscription_status === 'active'}
          />
          <PlanCard
            title="Pro"
            price="£25"
            subtitle="unlimited clients — best value for growing practices"
            features={FEATURES_PRO}
            tier="pro"
            recommended={true}
            onSelect={handleSelect}
            loading={loadingTier === 'pro'}
            isExisting={existingSub?.subscription_status === 'active'}
            isCurrent={existingSub?.subscription_tier === 'pro' && existingSub?.subscription_status === 'active'}
          />
        </div>

        {/* Trust line */}
        <div style={{ textAlign: 'center', marginTop: 40, fontSize: '0.82rem', color: 'var(--ink-faint)' }}>
          Cancel any time · Secure payments via Stripe · 7-year file retention included
        </div>

        {isNew && (
          <div style={{ textAlign: 'center', marginTop: 20 }}>
            <Link to="/dashboard" style={{ fontSize: '0.85rem', color: 'var(--ink-muted)' }}>
              Continue with free trial — choose a plan later →
            </Link>
          </div>
        )}
      </main>

      {/* Plan switch confirmation modal */}
      {confirmTier && confirmDetails && (
        <div className="modal-backdrop" onClick={() => setConfirmTier(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <h3>{confirmDetails.title}</h3>
              <button className="btn btn-ghost btn-icon" onClick={() => setConfirmTier(null)}>✕</button>
            </div>
            <div className="modal-body">

              {/* Summary table */}
              <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: '16px 18px', marginBottom: 18 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, fontSize: '0.88rem' }}>
                  <span style={{ color: 'var(--ink-muted)' }}>Current plan</span>
                  <span style={{ fontWeight: 500, textDecoration: 'line-through', color: 'var(--ink-muted)' }}>{confirmDetails.from}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, fontSize: '0.88rem' }}>
                  <span style={{ color: 'var(--ink-muted)' }}>New plan</span>
                  <span style={{ fontWeight: 600, color: 'var(--accent)' }}>{confirmDetails.to}</span>
                </div>
                <div style={{ borderTop: '1px solid var(--surface-3)', paddingTop: 12, display: 'flex', justifyContent: 'space-between', fontSize: '0.95rem' }}>
                  <span style={{ fontWeight: 600 }}>First charge on new plan</span>
                  <span style={{ fontWeight: 700 }}>{confirmDetails.charge}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: '0.82rem', color: 'var(--ink-muted)' }}>
                  <span>Charged on</span>
                  <span>{confirmDetails.when}</span>
                </div>
              </div>

              <div style={{ background: 'var(--blue-light)', border: '1.5px solid var(--blue)', borderRadius: 'var(--radius)', padding: '12px 16px', fontSize: '0.83rem', color: 'var(--blue)' }}>
                ℹ {confirmDetails.note}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setConfirmTier(null)}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={doSwitch}
                disabled={loadingTier !== null}
              >
                {loadingTier ? 'Switching…' : 'Confirm plan change'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
